import type { ComponentProps } from 'react';
import type { Notification } from '@packages/shared/src/ui/Notification/Notification';
import type { CheckoutMessage } from '@packages/gql/generated/shopping/graphql';

export type NotificationSeverity = ComponentProps<typeof Notification>['severity'];

export type DisplayMessage = { text: string; severity: NotificationSeverity };

/**
 * Maps a backend message level (e.g. GQL CheckoutMessageLevel) to a Notification severity.
 * `fallback` is returned for an unrecognized or missing level (default `'error'`).
 */
export const mapMessageLevelToSeverity = (
  level: string | null | undefined,
  fallback: NotificationSeverity = 'error',
): NotificationSeverity => {
  switch (level?.toLowerCase()) {
    case 'error':
      return 'error';
    case 'warning':
      return 'warning';
    case 'success':
      return 'success';
    case 'info':
      return 'info';
    default:
      return fallback;
  }
};

/** Turns a basket message list into displayable notifications, keeping their level. */
export const toDisplayMessages = (
  elements: readonly CheckoutMessage[] | null | undefined,
): DisplayMessage[] =>
  elements?.map((element) => ({
    text: element.description,
    severity: mapMessageLevelToSeverity(element.level),
  })) ?? [];
